import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, X, FileText } from 'lucide-react';
import NavTabs from '../../components/navtabs/NavTabs';
import { GetAnnouncement, AnnouncementItem, GetAnnouncementTypes, AnnouncementTypeItem } from '../../services/announcementService';
import { resolveImageUrl } from '../../utils/imageHelper';
import Dropdown from '../../components/dropdown/Index';
import useMallTenantDropdown from '../../hooks/useMallTenantDropdown';

interface NewsItem {
  id: number;
  title: string;
  description: string;
  image: string;
  documentUrl?: string;
  typeName: string;
}

interface TabItem {
  label: string;
  path: string;
  typeId: number;
}

const Announcements = () => {
  const location = useLocation();
  const {
    mallOptions,
    tenantOptions,
    selectedMall,
    selectedTenant,
    setSelectedMall,
    setSelectedTenant,
  } = useMallTenantDropdown();
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedNews, setSelectedNews] = useState<NewsItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newsData, setNewsData] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [tabs, setTabs] = useState<TabItem[]>([]);
  const [activeTypeId, setActiveTypeId] = useState<number>(0);
  const [, setLoadingTabs] = useState(true);

  const itemsPerPage = 9;

  const fetchAnnouncementTypes = async () => {
    try {
      setLoadingTabs(true);
      const response = await GetAnnouncementTypes();

      if (response && response.Code === 0 && response.dropdownClassDatas) {
        const basePath = '/tenant-relation/announcements';
        const transformedTabs: TabItem[] = response.dropdownClassDatas.map((item: AnnouncementTypeItem, index: number) => ({
          label: item.DataLabel,
          path: index === 0 ? basePath : `${basePath}?type=${item.DataId}`,
          typeId: item.DataId
        }));

        setTabs(transformedTabs);
        if (transformedTabs.length > 0) {
          setActiveTypeId(transformedTabs[0].typeId);
        }
      }
    } catch (error) {
      console.error('Error fetching announcement types:', error);
    } finally {
      setLoadingTabs(false);
    }
  };

  const fetchAnnouncements = async (page: number, typeId: number) => {
    try {
      setLoading(true);
      const response = await GetAnnouncement(
        0,
        typeId,
        page,
        itemsPerPage
      );

      if (response && response.Code === 0 && response.announcementDatas) {
        const transformedData: NewsItem[] = response.announcementDatas.map((item: AnnouncementItem) => ({
          id: item.AnnouncementId,
          title: item.AnnouncementName,
          description: item.AnnouncementDescription,
          image: resolveImageUrl(item.ImageURL),
          documentUrl: item.DocumentURL,
          typeName: item.AnnouncementTypeName
        }));

        setNewsData(transformedData);
        setTotalRecords(response.TotalRecord);
        setTotalPages(Math.max(1, Math.ceil(response.TotalRecord / itemsPerPage)));
      } else {
        setNewsData([]);
        setTotalRecords(0);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Error fetching announcements:', error);
      setNewsData([]);
      setTotalRecords(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnouncementTypes();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const typeParam = params.get('type');

    if (typeParam) {
      const typeId = parseInt(typeParam);
      setActiveTypeId(typeId);
      setCurrentPage(1);
    } else if (tabs.length > 0) {
      setActiveTypeId(tabs[0].typeId);
      setCurrentPage(1);
    }
  }, [location.search, tabs]);

  useEffect(() => {
    if (activeTypeId > 0 && selectedMall && selectedTenant) {
      fetchAnnouncements(currentPage, activeTypeId);
    }
  }, [currentPage, activeTypeId, selectedMall, selectedTenant]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedMall, selectedTenant]);

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCardClick = (news: NewsItem) => {
    setSelectedNews(news);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedNews(null);
  };

  const handleDocumentDownload = (documentUrl: string) => {
    window.open(documentUrl, '_blank');
  };

  const renderPaginationNumbers = () => {
    const pages: (number | string)[] = [];

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    const left = Math.max(2, currentPage - 1);
    const right = Math.min(totalPages - 1, currentPage + 1);

    pages.push(1);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push('...');
    pages.push(totalPages);

    return pages;
  };

  return (
    <>
      <Dropdown
        mallSelected={setSelectedMall}
        mallOptions={mallOptions}
        tenantSelected={setSelectedTenant}
        tenantOptions={tenantOptions}
        mallValue={selectedMall}
        tenantValue={selectedTenant}
      />
      <div className="min-h-screen bg-white dark:bg-gray-900 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        
        <div className="flex justify-end overflow-x-auto pb-1 mb-4 sm:mb-6">
          <NavTabs tabs={tabs} />
        </div>

        
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8">
            {[...Array(itemsPerPage)].map((_, index) => (
              <div key={index} className="bg-white dark:bg-gray-800 rounded-3xl shadow-sm overflow-hidden animate-pulse">
                <div className="aspect-[4/3] bg-gray-200 dark:bg-gray-700"></div>
                <div className="p-4 sm:p-6">
                  <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded mb-3"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8">
            {newsData.map((news) => (
              <div
                key={news.id}
                className="bg-white dark:bg-gray-800 rounded-3xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer group"
                onClick={() => handleCardClick(news)}
              >
                <div className="aspect-[4/3] bg-gray-100 dark:bg-gray-700 overflow-hidden relative">
                  <img
                    src={news.image}
                    alt={news.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const nextSibling = target.nextSibling as HTMLElement;
                      if (nextSibling) nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-full h-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center hidden">
                    <div className="text-gray-400 text-6xl opacity-50"></div>
                  </div>
                </div>

                <div className="p-4 sm:p-6">
                  <h3 className="font-bold text-lg sm:text-xl text-gray-900 dark:text-white mb-2 sm:mb-3 line-clamp-2 leading-tight">
                    {news.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-3 sm:mb-4 line-clamp-3 leading-relaxed">
                    {news.description}
                  </p>
                </div>
              </div>
            ))}

            {newsData.length === 0 && (
              <div className="col-span-full text-center text-gray-500 dark:text-gray-400 py-16">
                <div className="text-6xl mb-4 opacity-50">📰</div>
                <p className="text-lg">Tidak ada berita di halaman ini.</p>
              </div>
            )}
          </div>
        )}

        
        {totalPages > 1 && (
          <div className="flex justify-center items-center flex-wrap gap-2 mt-8">
            <button
              onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              aria-label="Previous page"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            {renderPaginationNumbers().map((page, index) => (
              <button
                key={index}
                onClick={() => typeof page === 'number' && handlePageChange(page)}
                disabled={page === '...'}
                aria-current={page === currentPage ? 'page' : undefined}
                className={`px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  currentPage === page
                    ? 'bg-blue-500 text-white'
                    : page === '...'
                    ? 'text-gray-400 cursor-default'
                    : 'text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 hover:bg-white dark:hover:bg-gray-700'
                }`}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              aria-label="Next page"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}

        {!loading && totalRecords > 0 && (
          <div className="text-center text-gray-500 dark:text-gray-400 text-sm mt-4">
            Menampilkan {((currentPage - 1) * itemsPerPage) + 1} dari {totalRecords} berita
          </div>
        )}
      </div>

      
      {isModalOpen && selectedNews && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4">
          <div
            className="absolute inset-0 backdrop-blur-sm bg-black/50 dark:bg-black/70"
            onClick={closeModal}
          ></div>

          <div
            className="relative bg-white dark:bg-gray-800 rounded-2xl w-full sm:max-w-xs shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeModal}
              className="absolute top-2.5 right-2.5 z-20 bg-black/50 hover:bg-black/70 text-white backdrop-blur-md rounded-full p-1.5 transition-colors shadow-md"
            >
              <X className="w-3.5 h-3.5" />
            </button>

            <img
              src={selectedNews.image}
              alt={selectedNews.title}
              className="w-full h-auto block rounded-t-2xl"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const nextSibling = target.nextSibling as HTMLElement;
                if (nextSibling) nextSibling.style.display = 'flex';
              }}
            />
            <div className="w-full h-48 bg-gray-200 dark:bg-gray-700 items-center justify-center hidden rounded-t-2xl">
              <div className="text-gray-400 text-5xl opacity-50"></div>
            </div>

            <div className="px-4 py-3 bg-white dark:bg-gray-800">
              <h1 className="font-bold text-sm sm:text-base text-gray-900 dark:text-white mb-1 leading-tight pr-5 line-clamp-2">
                {selectedNews.title}
              </h1>
              <p className="text-gray-500 dark:text-gray-400 text-xs leading-snug line-clamp-3">
                {selectedNews.description}
              </p>
              {selectedNews.documentUrl && (
                <div className="flex justify-end mt-2">
                  <button
                    onClick={() => handleDocumentDownload(selectedNews.documentUrl!)}
                    className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-1 px-2.5 rounded-lg transition-colors text-xs"
                    title="Download PDF"
                  >
                    <FileText className="w-3 h-3" />
                    <span>PDF</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default Announcements;